import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Search, X, LogOut, Settings as SettingsIcon, Bookmark, Heart, Users, BarChart3, Bell, Shuffle, UserPlus, Menu, ChevronDown, Home, HelpCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { MuteToggle, ShuffleButton, useShuffle } from "./NavAudioShuffle";
import api from "../lib/api";
import Wordmark from "./Wordmark";
import Avatar from "./Avatar";
import GenreMenu from "./GenreMenu";
import { GENRES } from "../lib/genres";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { layout } = useTheme();
  const nav = layout.nav;
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileGenres, setMobileGenres] = useState(false);

  useEffect(() => { setMobileOpen(false); setMobileGenres(false); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Explicit submit only: typing never searches. Runs on Enter / Search button,
  // with a 2-character minimum applied at submit time.
  const go = () => {
    const query = q.trim();
    if (query.length >= 2 && query.toLowerCase() !== (user?.email || "").toLowerCase()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };
  const initial = (user?.name || user?.email || "U").charAt(0).toUpperCase();
  const boxy = nav !== "cinematic";

  const header = {
    cinematic: `transition-colors duration-300 ${scrolled ? "bg-[var(--bg-glass)] backdrop-blur-xl border-b border-[var(--border-subtle)]" : "bg-gradient-to-b from-black/80 to-transparent"}`,
    utility: "bg-[var(--bg-surface)] border-b-2 border-[var(--accent)]",
    float: "bg-gradient-to-b from-black/70 to-transparent",
    guide: "bg-[var(--bg-surface)] border-b border-[var(--border-subtle)]",
    aisle: "bg-[var(--bg-surface)] border-b border-[var(--border-subtle)]",
    flagship: "bg-[var(--bg-surface)] border-b border-[var(--border-subtle)]",
    scanline: "relative overflow-hidden bg-[var(--bg-surface)] border-b-2 border-[var(--border-accent)] scanlines",
    wheel: "bg-[var(--bg-surface)] border-b-2 border-[var(--text-primary)]",
  }[nav];
  const shuffle = useShuffle();

  const linkCls = (path) => {
    const active = location.pathname === path;
    if (nav === "wheel") return `whitespace-nowrap rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider ${active ? "bg-[var(--wheel-bar)] text-[var(--wheel-bar-text)]" : "text-[var(--text-secondary)] hover:bg-[var(--wheel-bar)] hover:text-[var(--wheel-bar-text)]"}`;
    if (nav === "utility") return `pb-1 border-b-2 text-sm font-semibold ${active ? "border-[var(--accent)] text-[var(--text-primary)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`;
    if (nav === "aisle") return `rounded-[var(--card-radius)] border border-[var(--border-accent)] bg-[var(--accent)]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider ${active ? "text-[var(--accent-hover)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`;
    if (nav === "flagship") return `text-base font-semibold ${active ? "text-[var(--accent-hover)] underline underline-offset-8 decoration-2" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`;
    if (nav === "float") return `text-sm tracking-widest uppercase ${active ? "text-[var(--accent-hover)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`;
    if (nav === "scanline") return `text-xs uppercase tracking-[0.22em] ${active ? "text-[var(--neon-cyan)]" : "text-[var(--text-secondary)] hover:text-[var(--accent-hover)]"}`;
    return `text-sm font-medium ${active ? "text-[var(--accent)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`;
  };

  const brand = (
    <Link to="/" className="inline-flex items-center" data-testid="nav-brand-logo">
      <Wordmark sizeClass={nav === "flagship" ? "text-lg sm:text-4xl" : "text-lg sm:text-3xl"} />
    </Link>
  );

  const searchBox = (
    <>
      <button
        type="button"
        onClick={() => navigate("/search")}
        aria-label="Search"
        className={`sm:hidden flex h-9 w-9 flex-none items-center justify-center border border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-[var(--accent)] ${boxy ? "rounded-[var(--card-radius)]" : "rounded-md"}`}
        data-testid="nav-search-mobile"
      >
        <Search size={18} />
      </button>
      <form className="hidden sm:flex items-center gap-2" role="search" onSubmit={(e) => { e.preventDefault(); go(); }}>
        <div className="relative flex items-center">
          <Search size={18} className="pointer-events-none absolute left-3 text-[var(--text-muted)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles..."
            type="search" name="dial-title-search" id="dial-title-search" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} inputMode="search"
            data-lpignore="true" data-1p-ignore="true" data-form-type="other" aria-label="Search titles"
            className={boxy
              ? "w-44 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] py-2 pl-9 pr-7 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] outline-none focus:border-[var(--accent)]"
              : "w-48 rounded-full border border-[var(--border-subtle)] bg-black/50 py-2 pl-9 pr-7 text-sm text-white placeholder-gray-500 outline-none transition-all focus:w-64 focus:border-[var(--accent)]"}
            data-testid="nav-search-input" />
          {q && <button type="button" onClick={() => setQ("")} className="absolute right-2 text-[var(--text-muted)] hover:text-[var(--text-primary)]" data-testid="nav-search-clear"><X size={15} /></button>}
        </div>
        <button type="submit"
          className={`flex flex-none items-center gap-1 ${boxy ? "rounded-[var(--card-radius)]" : "rounded-full"} border border-[var(--accent)] bg-[var(--accent)] px-3 py-2 text-sm font-semibold text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]`}
          data-testid="nav-search-button"><Search size={15} /> <span>Search</span></button>
      </form>
    </>
  );

  const profile = (
    <div className="relative">
      <button onClick={() => setMenuOpen((o) => !o)} className={`flex h-9 w-9 items-center justify-center overflow-hidden ${boxy ? "rounded-[var(--card-radius)]" : "rounded-md"}`} data-testid="nav-profile-button">
        <Avatar user={user} size={36} rounded={boxy ? "rounded-[var(--card-radius)]" : "rounded-md"} />
      </button>
      {menuOpen && (<>
        <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
        <div className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] shadow-2xl" data-testid="nav-profile-menu">
          <div className="flex items-center gap-3 border-b border-[var(--border-subtle)] px-4 py-3"><Avatar user={user} size={36} className="flex-none" /><div className="min-w-0"><p className="truncate text-sm font-semibold text-[var(--text-primary)]">{user?.name}</p><p className="truncate text-xs text-[var(--text-secondary)]">{user?.email}</p></div></div>
          <button onClick={() => { setMenuOpen(false); navigate("/my-list"); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--accent)]/10" data-testid="menu-my-list"><Bookmark size={16} /> My List</button>
          <button onClick={() => { setMenuOpen(false); navigate(user?.username ? `/profile/${user.username}` : "/settings"); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--accent)]/10" data-testid="menu-my-profile"><Users size={16} /> My Profile</button>
          <button onClick={() => { setMenuOpen(false); navigate("/friends"); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--accent)]/10" data-testid="menu-friends"><UserPlus size={16} /> Friends</button>
          <button onClick={() => { setMenuOpen(false); navigate("/settings"); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--accent)]/10" data-testid="menu-settings"><SettingsIcon size={16} /> Settings</button>
          <button onClick={() => { setMenuOpen(false); navigate("/faq"); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--accent)]/10" data-testid="menu-faq"><HelpCircle size={16} /> FAQ</button>
          {user?.is_admin && <button onClick={() => { setMenuOpen(false); navigate("/admin/users"); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--accent)]/10" data-testid="menu-user-management"><Users size={16} /> User Management</button>}
          {user?.is_admin && <button onClick={() => { setMenuOpen(false); navigate("/admin/analytics"); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--text-primary)] hover:bg-[var(--accent)]/10" data-testid="menu-analytics"><BarChart3 size={16} /> Analytics</button>}
          <button onClick={() => { setMenuOpen(false); logout(); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-[var(--accent-hover)] hover:bg-[var(--accent)]/10" data-testid="menu-logout"><LogOut size={16} /> Log Out</button>
        </div>
      </>)}
    </div>
  );

  const services = user?.services || [];
  const SVC = { netflix: "NETFLIX", hulu: "HULU", appletv: "APPLE TV+", disney: "DISNEY+", max: "MAX", prime: "PRIME VIDEO", paramount: "PARAMOUNT+", peacock: "PEACOCK", youtubetv: "YOUTUBE TV" };

  return (
    <>
    <header className={`sticky top-0 z-50 w-full ${nav === "guide" ? "border-t-2 border-[var(--accent)]" : ""} ${header}`} data-testid="main-navbar" data-nav-variant={nav}>
      {nav === "guide" && (
        <div className="flex items-center justify-between border-b border-[var(--border-subtle)] px-4 lg:px-14 py-1 text-[11px] uppercase tracking-widest text-[var(--accent-hover)]" data-testid="guide-strip">
          <span>{new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
          <span className="hidden truncate sm:block text-[var(--text-secondary)]">CH: {services.length ? services.map((s) => SVC[s] || s).join("  ·  ") : "ALL"}</span>
        </div>
      )}
      <div className={`relative z-10 flex items-center gap-2.5 sm:gap-4 px-4 lg:px-14 ${nav === "flagship" ? "py-4" : "py-3"}`}>
        <button
          onClick={() => setMobileOpen((o) => !o)}
          className="md:hidden flex h-9 w-9 flex-none items-center justify-center rounded-[var(--card-radius)] border border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-[var(--accent)] hover:text-[var(--accent-hover)]"
          aria-label="Menu" aria-expanded={mobileOpen} data-testid="mobile-menu-button">
          {mobileOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        {brand}
        {nav === "profile" ? (
          <nav className="profile-nav hidden items-center md:flex" data-testid="profile-nav">
            <Link to="/" className="profile-navlink" data-testid="nav-home-link">Home</Link><span className="profile-pipe">|</span>
            <Link to="/" className="profile-navlink">Browse</Link><span className="profile-pipe">|</span>
            <GenreMenu linkClassName="profile-navlink inline-flex items-center gap-1" /><span className="profile-pipe">|</span>
            <Link to="/my-list" className="profile-navlink" data-testid="nav-my-list-link">My List</Link><span className="profile-pipe">|</span>
            <Link to="/favorites" className="profile-navlink" data-testid="nav-favorites-link">Favorites</Link><span className="profile-pipe">|</span>
            <button onClick={() => alert("Messages are coming soon ✉")} className="profile-navlink">Messages</button><span className="profile-pipe">|</span>
            <Link to="/settings" className="profile-navlink">Profile</Link><span className="profile-pipe">|</span>
            {user?.is_admin && (<><Link to="/admin" className="profile-navlink" data-testid="nav-admin-link">Extras</Link><span className="profile-pipe">|</span></>)}
            <button onClick={() => logout()} className="profile-navlink" data-testid="nav-signout-link">Sign Out</button>
          </nav>
        ) : (
          <nav className={`hidden md:flex items-center gap-3 ${nav === "float" ? "ml-6 gap-6" : "ml-6"}`}>
            <Link to="/" className={linkCls("/")} data-testid="nav-home-link">Home</Link>
            <Link to="/my-list" className={linkCls("/my-list")} data-testid="nav-my-list-link">My List</Link>
            <Link to="/favorites" className={linkCls("/favorites")} data-testid="nav-favorites-link">Favorites</Link>
            <GenreMenu linkClassName={`inline-flex items-center gap-1 ${linkCls("/genre")}`} />
            {user?.is_admin && <Link to="/admin" className={linkCls("/admin")} data-testid="nav-admin-link">Admin</Link>}
          </nav>
        )}
        <div className="ml-auto flex items-center gap-1.5 sm:gap-3"><MuteToggle /><ShuffleButton className="hidden md:flex" />{searchBox}<FriendsBell navigate={navigate} boxy={boxy} />{profile}</div>
      </div>

      {mobileOpen && (
        <div className="md:hidden">
          <div className="fixed inset-0 z-40" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-overlay" />
          <nav className="relative z-50 border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] shadow-2xl" data-testid="mobile-nav-menu">
            <Link to="/" className="block px-5 py-3.5 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--accent)]/10 hover:text-[var(--accent-hover)]" data-testid="mobile-nav-home">Home</Link>
            <Link to="/my-list" className="block border-t border-[var(--border-subtle)] px-5 py-3.5 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--accent)]/10 hover:text-[var(--accent-hover)]" data-testid="mobile-nav-my-list">My List</Link>
            <Link to="/favorites" className="block border-t border-[var(--border-subtle)] px-5 py-3.5 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--accent)]/10 hover:text-[var(--accent-hover)]" data-testid="mobile-nav-favorites">Favorites</Link>
            <button onClick={() => setMobileGenres((o) => !o)} className="flex w-full items-center justify-between border-t border-[var(--border-subtle)] px-5 py-3.5 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--accent)]/10 hover:text-[var(--accent-hover)]" data-testid="mobile-nav-genres-toggle">
              Genres <ChevronDown size={16} className={`transition-transform ${mobileGenres ? "rotate-180" : ""}`} />
            </button>
            {mobileGenres && (
              <div className="grid grid-cols-2 gap-1 border-t border-[var(--border-subtle)] bg-[var(--bg-base)] p-2" data-testid="mobile-nav-genres-list">
                {GENRES.map((g) => (
                  <button key={g.slug} onClick={() => { setMobileOpen(false); navigate(`/genre/${g.slug}`); }}
                    className="rounded-[var(--card-radius)] px-3 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-[var(--accent)]/10 hover:text-[var(--accent-hover)]"
                    data-testid={`mobile-nav-genre-${g.slug}`}>{g.name}</button>
                ))}
              </div>
            )}
            {user?.is_admin && <Link to="/admin" className="block border-t border-[var(--border-subtle)] px-5 py-3.5 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--accent)]/10 hover:text-[var(--accent-hover)]" data-testid="mobile-nav-admin">Admin</Link>}
          </nav>
        </div>
      )}
    </header>

    <nav className="fixed inset-x-0 bottom-0 z-50 flex items-stretch border-t border-[var(--border-subtle)] bg-[var(--bg-surface)] md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
      {[
        { to: "/", label: "Home", Icon: Home, tid: "bottom-nav-home" },
        { to: "/my-list", label: "My List", Icon: Bookmark, tid: "bottom-nav-my-list" },
        { to: "/favorites", label: "Favorites", Icon: Heart, tid: "bottom-nav-favorites" },
        { to: "/search", label: "Search", Icon: Search, tid: "bottom-nav-search" },
      ].map(({ to, label, Icon, tid }) => {
        const active = location.pathname === to;
        return (
          <Link key={to} to={to} data-testid={tid}
            className={`flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold ${active ? "text-[var(--accent-hover)]" : "text-[var(--text-secondary)]"}`}>
            <Icon size={20} className={active ? "text-[var(--accent-hover)]" : ""} />
            {label}
          </Link>
        );
      })}
      <button type="button" onClick={shuffle} data-testid="bottom-nav-shuffle"
        className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold text-[var(--text-secondary)]">
        <Shuffle size={20} />
        Shuffle
      </button>
    </nav>
    </>
  );
}

function FriendsBell({ navigate, boxy }) {
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/notifications").then(({ data }) => { if (alive) setUnread(data.unread || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("dial-notify-refresh", load);
    return () => { alive = false; clearInterval(t); window.removeEventListener("focus", onFocus); window.removeEventListener("dial-notify-refresh", load); };
  }, []);
  return (
    <button onClick={() => navigate("/friends/requests")} title="Friend requests" data-testid="nav-notifications-bell"
      className={`relative flex h-9 w-9 items-center justify-center border border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-[var(--accent)] ${boxy ? "rounded-[var(--card-radius)]" : "rounded-md"}`}>
      <Bell size={18} />
      {unread > 0 && <span className="nav-badge" data-testid="nav-notifications-badge">{unread > 9 ? "9+" : unread}</span>}
    </button>
  );
}